// Compat shim — Phase 2 PR1 of the adaptive-lookup-field refactor.
// See `docs/audits/adaptive-field-refactor-proposal.md` §3.2.
//
// The single-select lookup component's implementation now lives in
// `AdaptiveLookupField`, which auto-branches on the collection doc's
// `isMultiSelect` flag. This wrapper keeps the historical `LookupFieldSelect`
// API alive so the ~30 admin single-select call sites (ReferenceEditor,
// BrandEditor, CalibreTiersEditor, ElectronicModuleEditorModal, BrandPortal
// panels, etc.) don't have to migrate at once — PR2 does the mechanical
// import rename. External-repo consumers are unaffected.
//
// The shim maps `value` → `singleValue` and `onChange` → `onSingleChange` and
// forwards everything else. If a call site's collection is flipped to multi
// in Firestore without swapping to `LookupMultiSelect`, AdaptiveLookupField
// dev-warns in the console and keeps rendering the single branch — the bound
// shape wins over a drifted flag, so the field stays usable. See the
// `effectiveMultiSelect` note in AdaptiveLookupField.

import { AdaptiveLookupField } from './AdaptiveLookupField';

interface LookupFieldSelectProps {
  label: string;
  value: string | undefined;
  lookupCollection: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  // When false, hide the create-new-lookup-value path (custom option +
  // CustomValueActions) and show a disabled "Request new value" affordance.
  // Defaults to true to preserve super-admin behavior.
  allowCustom?: boolean;
  // Brand Portal Phase 2.5: when provided (and allowCustom===false), the
  // "Request new value →" affordance becomes a live button that calls this with
  // the current value as a seed. The parent owns the request modal.
  onRequestNewValue?: (seed: { query?: string }) => void;
  // Show an inline "Request sent" confirmation next to the affordance.
  requestSent?: boolean;
}

export function LookupFieldSelect({
  label,
  value,
  lookupCollection,
  onChange,
  placeholder,
  disabled,
  allowCustom,
  onRequestNewValue,
  requestSent,
}: LookupFieldSelectProps) {
  return (
    <AdaptiveLookupField
      label={label}
      lookupCollection={lookupCollection}
      singleValue={value}
      onSingleChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      allowCustom={allowCustom}
      onRequestNewValue={onRequestNewValue}
      requestSent={requestSent}
    />
  );
}
